import { parseEngineNumberFormat } from "./formatCell"
import { NumberFormatSpec, StatKind } from "./tableModel.types"

/** Statistic label of the significance letters row. */
export const SIG_STAT = "sig"

/** Engine label of the corner cell next to the statistic labels. */
export const STATISTIC_LABEL = "Statistic"

export interface StatRegistryEntry {
  kind: StatKind
  /** The engine's own hint for this stat, used when the grid carries none. */
  defaultFormat: string
  /** Show as a percent whatever the engine hint says (DTAB `perc` is a fraction hinted "0.0"). */
  forcePercent?: boolean
}

/** Engine statistic labels (XTab.h, DTab.h, plus the significance `sig` row). */
export const STAT_REGISTRY: Readonly<Record<string, StatRegistryEntry>> = {
  // XTAB
  n: { kind: "count", defaultFormat: "0,0" },
  N: { kind: "weighted", defaultFormat: "0,0.0" },
  "perc(%)": { kind: "percent", defaultFormat: "0.0%", forcePercent: true },
  index: { kind: "index", defaultFormat: "0,0.0" },
  "row%": { kind: "percent", defaultFormat: "0.0%", forcePercent: true },
  "col%": { kind: "percent", defaultFormat: "0.0%", forcePercent: true },
  // DTAB
  "base n": { kind: "count", defaultFormat: "0,0" },
  "base N": { kind: "weighted", defaultFormat: "0,0.0" },
  avg: { kind: "mean", defaultFormat: "0,0.0" },
  min: { kind: "minmax", defaultFormat: "0.0" },
  max: { kind: "minmax", defaultFormat: "0.0" },
  perc: { kind: "percent", defaultFormat: "0.0", forcePercent: true },
  qual: { kind: "text", defaultFormat: "" },
  "CRR%": { kind: "percent", defaultFormat: "0.0%", forcePercent: true },
  // Shown beside the mean, so a style's mean decimals apply to them too.
  stddev: { kind: "mean", defaultFormat: "0,0.00" },
  stderr: { kind: "mean", defaultFormat: "0,0.00" },
  median: { kind: "mean", defaultFormat: "0,0.0" },
  // A total of the values, shown like a count: no style sets its decimals.
  sum: { kind: "count", defaultFormat: "0,0" },
  // Significance letters row
  [SIG_STAT]: { kind: "letters", defaultFormat: "" },
}

/** Stat labels that only occur in an XTAB. */
export const XTAB_ONLY_STATS: readonly string[] = [
  "col%",
  "row%",
  "index",
  "perc(%)",
]

/** Stat labels that only occur in a DTAB. */
export const DTAB_ONLY_STATS: readonly string[] = [
  "avg",
  "base n",
  "base N",
  "perc",
  "CRR%",
  "min",
  "max",
  "qual",
  "stddev",
  "stderr",
  "median",
  "sum",
]

/**
 * Resolves a statistic's kind and final number format.
 * @param stat statistic label from the grid ("" when the table has none)
 * @param engineHint the `"number"` hint after cell > row > column > table
 *   precedence, or undefined when no level sets one
 */
export const resolveStatFormat = (
  stat: string,
  engineHint: string | undefined
): { kind: StatKind; format: NumberFormatSpec | null } => {
  // Own keys only: a stat named "constructor" or "toString" is not a known stat.
  const entry = Object.hasOwn(STAT_REGISTRY, stat) ? STAT_REGISTRY[stat] : undefined
  if (!entry) {
    // Unknown stat: follow the engine hint as-is.
    const format =
      engineHint === undefined ? null : parseEngineNumberFormat(engineHint)
    const kind: StatKind = !format ? "text" : format.percent ? "percent" : "count"
    return { kind, format }
  }

  if (entry.kind === "letters" || entry.kind === "text") {
    return { kind: entry.kind, format: null }
  }

  const parsed =
    engineHint === undefined ? null : parseEngineNumberFormat(engineHint)
  const base = parsed ?? parseEngineNumberFormat(entry.defaultFormat)
  if (!base) return { kind: entry.kind, format: null }
  return {
    kind: entry.kind,
    format: entry.forcePercent ? { ...base, percent: true } : base,
  }
}
